// Color extraction for the Color Palette extension. Pure functions first
// (testable without a DOM), then the browser loader with its cache.

export type RGB = [number, number, number];

export interface Swatch {
  rgb: RGB;
  hex: string;
  /** Share of the image's opaque pixels this color stands for, 0 to 1. */
  share: number;
}

export type ColorFormat = 'hex' | 'rgb';

/** Most colors any setting can ask for; the cache keeps this many per image. */
export const MAX_COLORS = 8;
/** The image is drawn this small before sampling. Plenty for a palette, and fast. */
const SAMPLE_SIZE = 96;
/** Median cut makes this many boxes, then near-duplicates merge away. */
const BOXES = 24;
/** Two colors closer than this (redmean distance) count as one. */
const MIN_DISTANCE = 48;

interface Bucket {
  r: number;
  g: number;
  b: number;
  count: number;
}

export function toHex([r, g, b]: RGB): string {
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

export function formatColor(swatch: Swatch, format: ColorFormat): string {
  const [r, g, b] = swatch.rgb;
  return format === 'rgb' ? `rgb(${r}, ${g}, ${b})` : swatch.hex;
}

/** Perceptual-ish distance between two colors ("redmean"), cheap and good enough for merging. */
export function colorDistance(a: RGB, b: RGB): number {
  const rMean = (a[0] + b[0]) / 2;
  const dr = a[0] - b[0];
  const dg = a[1] - b[1];
  const db = a[2] - b[2];
  return Math.sqrt((2 + rMean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rMean) / 256) * db * db);
}

/** True when a color is light enough to need a ring to stand out on a light background. */
export function isLight([r, g, b]: RGB): boolean {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 220;
}

function channelRange(box: Bucket[], channel: 'r' | 'g' | 'b'): number {
  let min = 255;
  let max = 0;
  for (const bucket of box) {
    const v = bucket[channel] / bucket.count;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return max - min;
}

function population(box: Bucket[]): number {
  return box.reduce((sum, bucket) => sum + bucket.count, 0);
}

function average(box: Bucket[]): RGB {
  let r = 0;
  let g = 0;
  let b = 0;
  let count = 0;
  for (const bucket of box) {
    r += bucket.r;
    g += bucket.g;
    b += bucket.b;
    count += bucket.count;
  }
  return [Math.round(r / count), Math.round(g / count), Math.round(b / count)];
}

/** Split along the widest channel at the population median. Null when the box can't split. */
function split(box: Bucket[]): [Bucket[], Bucket[]] | null {
  if (box.length < 2) return null;
  const ranges = { r: channelRange(box, 'r'), g: channelRange(box, 'g'), b: channelRange(box, 'b') };
  const channel = (Object.keys(ranges) as Array<'r' | 'g' | 'b'>).reduce((a, c) => (ranges[c] > ranges[a] ? c : a));
  const sorted = [...box].sort((a, b) => a[channel] / a.count - b[channel] / b.count);
  const half = population(sorted) / 2;
  let running = 0;
  let cut = 1;
  for (let i = 0; i < sorted.length - 1; i++) {
    running += sorted[i].count;
    cut = i + 1;
    if (running >= half) break;
  }
  return [sorted.slice(0, cut), sorted.slice(cut)];
}

/**
 * The image's main colors, most common first, from RGBA pixel data (as from
 * getImageData). Median cut over a 15-bit histogram, then colors that look
 * alike merge so the palette shows distinct colors rather than five beiges.
 */
export function extractPalette(data: ArrayLike<number>, count = 5): Swatch[] {
  const buckets = new Map<number, Bucket>();
  let total = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    if (data[i + 3] < 125) continue; // transparent
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.r += r;
      bucket.g += g;
      bucket.b += b;
      bucket.count++;
    } else {
      buckets.set(key, { r, g, b, count: 1 });
    }
    total++;
  }
  if (total === 0) return [];

  // Split the box that most deserves it: by population first, then favour
  // boxes that also span a wide range, so small vivid accents get a box too.
  const boxes: Bucket[][] = [[...buckets.values()]];
  const score = (box: Bucket[], byVolume: boolean) => {
    const n = population(box);
    if (!byVolume) return n;
    return n * (channelRange(box, 'r') + 1) * (channelRange(box, 'g') + 1) * (channelRange(box, 'b') + 1);
  };
  while (boxes.length < BOXES) {
    const byVolume = boxes.length >= BOXES * 0.6;
    let best = -1;
    let bestScore = 0;
    boxes.forEach((box, i) => {
      if (box.length < 2) return;
      const s = score(box, byVolume);
      if (s > bestScore) {
        bestScore = s;
        best = i;
      }
    });
    if (best === -1) break;
    const halves = split(boxes[best]);
    if (!halves) break;
    boxes.splice(best, 1, ...halves);
  }

  // Merge look-alikes into the more common color, then keep the most common
  const candidates = boxes
    .map((box) => ({ rgb: average(box), count: population(box) }))
    .sort((a, b) => b.count - a.count);
  const picked: Array<{ rgb: RGB; count: number }> = [];
  for (const candidate of candidates) {
    const near = picked.find((p) => colorDistance(p.rgb, candidate.rgb) < MIN_DISTANCE);
    if (near) near.count += candidate.count;
    else picked.push({ ...candidate });
  }

  return picked
    .sort((a, b) => b.count - a.count)
    .slice(0, Math.max(1, count))
    .map(({ rgb, count: n }) => ({ rgb, hex: toHex(rgb), share: n / total }));
}

// ---------------------------------------------------------------------------
// Browser loader
// ---------------------------------------------------------------------------

const cache = new Map<string, Promise<Swatch[] | null>>();

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Without CORS the canvas is tainted and its pixels can't be read
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Could not load ${url}`));
    img.src = url;
  });
}

async function computePalette(url: string): Promise<Swatch[] | null> {
  try {
    const img = await loadImage(url);
    const scale = Math.min(1, SAMPLE_SIZE / Math.max(img.naturalWidth, img.naturalHeight));
    const width = Math.max(1, Math.round(img.naturalWidth * scale));
    const height = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, width, height);
    const palette = extractPalette(ctx.getImageData(0, 0, width, height).data, MAX_COLORS);
    return palette.length > 0 ? palette : null;
  } catch (e) {
    // Hosts that don't allow cross-origin reads: no palette for this image
    console.warn('[ColorPalette] Could not read colors:', e);
    return null;
  }
}

/** The image's palette (up to MAX_COLORS), computed once per URL per session. Null if unreadable. */
export function getImagePalette(url: string): Promise<Swatch[] | null> {
  let pending = cache.get(url);
  if (!pending) {
    pending = computePalette(url);
    cache.set(url, pending);
  }
  return pending;
}
